"use client";

import { useEffect, useState, type ReactNode } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import styles from "./RangeSlider.module.css";

/**
 * RangeSlider - dwuuchwytowy slider zakresu (min/max) na `rc-slider`.
 *
 * **Zastosowanie:** filtry (cena, wielkosc, waga, itp.) gdzie user wybiera
 * podprzedzial [min..max]. Wizualnie pokazuje aktualne wartosci nad slider'em.
 *
 * **Uncontrolled + hidden inputs (dla formularzy):** poda`nameMin`/`nameMax` -
 * komponent renderuje pod-schowane `<input type="hidden">` z aktualnymi
 * wartosciami, ktore leca w FormData przy submit. Idealne do wzorca "form
 * z FormData" (jak `WShopAdditionalSearchOptions`).
 *
 * **Controlled:** `value={[min, max]}` + `onChange` - rodzic zarzadza state'em.
 *
 * **A11y:** rc-slider generuje ARIA (`role="slider"`, `aria-valuemin/max/now`)
 * na uchwytach. Uchwyty klawiaturowe (arrows, home/end).
 */
export function RangeSlider({
  min,
  max,
  step = 1,
  defaultValue,
  value,
  onChange,
  formatValue,
  nameMin,
  nameMax,
  disabled,
  ariaLabelMin,
  ariaLabelMax,
}: RangeSliderProps) {
  const initialRange: [number, number] = value ?? defaultValue ?? [min, max];
  const [internal, setInternal] = useState<[number, number]>(initialRange);

  /* Sync internal state z propsem `value` (controlled mode). Bez sync'a
     controlled slider zignorowalby zmiany rodzica po pierwszym renderze. */
  useEffect(() => {
    if (value) setInternal(value);
  }, [value]);

  /* Sync z `min`/`max` gdy zmienia sie zakres (np. nowy fetch API) w trybie
     uncontrolled - wtedy defaultValue moze byc stale, ale bounds sie zmienily.
     Zerujemy do pelnego zakresu tylko gdy internal wychodzi poza bounds. */
  useEffect(() => {
    if (value) return;
    setInternal(([lo, hi]) => {
      const nextLo = Math.max(min, Math.min(hi, lo));
      const nextHi = Math.min(max, Math.max(lo, hi));
      return nextLo === lo && nextHi === hi ? [lo, hi] : [nextLo, nextHi];
    });
  }, [min, max, value]);

  const handleChange = (next: number | number[]) => {
    if (!Array.isArray(next) || next.length !== 2) return;
    const range: [number, number] = [next[0], next[1]];
    setInternal(range);
    onChange?.(range);
  };

  const [current_lo, current_hi] = internal;
  const format = formatValue ?? String;

  return (
    <div className={styles.root}>
      <div className={styles.values}>
        <span className={styles.value}>{format(current_lo)}</span>
        <span className={styles.separator}>-</span>
        <span className={styles.value}>{format(current_hi)}</span>
      </div>

      <div className={styles.sliderWrap}>
        <Slider
          range
          min={min}
          max={max}
          step={step}
          value={internal}
          onChange={handleChange}
          allowCross={false}
          disabled={disabled}
          ariaLabelForHandle={
            ariaLabelMin && ariaLabelMax
              ? [ariaLabelMin, ariaLabelMax]
              : undefined
          }
        />
      </div>

      {nameMin != null && (
        <input type="hidden" name={nameMin} value={current_lo} />
      )}
      {nameMax != null && (
        <input type="hidden" name={nameMax} value={current_hi} />
      )}
    </div>
  );
}

export type RangeSliderProps = {
  /** Dolna granica zakresu (nie mozna zejsc ponizej). */
  min: number;
  /** Gorna granica zakresu (nie mozna wyjsc powyzej). */
  max: number;
  /** Krok (default 1). Dla ceny np. `0.01`. */
  step?: number;
  /** Wartosc poczatkowa dla uncontrolled - `[lo, hi]`. Default `[min, max]`. */
  defaultValue?: [number, number];
  /** Wartosc controlled - rodzic zarzadza state'em przez `onChange`. */
  value?: [number, number];
  /** Callback zmiany zakresu (odpalany na kazdy drag / step). */
  onChange?: (range: [number, number]) => void;
  /** Formatter wyswietlanych wartosci (np. `(v) => v.toFixed(2) + " zl"`). */
  formatValue?: (value: number) => ReactNode;
  /** Nazwa hidden input'a dla dolnej wartosci (submit w formularzu). */
  nameMin?: string;
  /** Nazwa hidden input'a dla gornej wartosci (submit w formularzu). */
  nameMax?: string;
  /** Wylacz interakcje. */
  disabled?: boolean;
  /** Aria-label dla dolnego uchwytu (a11y - screen reader). */
  ariaLabelMin?: string;
  /** Aria-label dla gornego uchwytu (a11y - screen reader). */
  ariaLabelMax?: string;
};
